import { ErrorCode, type SessionInfo } from '@sahab/contracts'
import { moduleEnabled } from '../../app/access'
import { refusal } from '../../app/guards'
import { ApiError } from '../../lib/api'
import {
  canOpenHub,
  canOpenSection,
  type LinkDef,
  type LinkId,
  SETTINGS_GROUPS,
  type SectionId,
  sectionDef,
} from './sections'

const forbidden = () => new ApiError({ status: 403, code: ErrorCode.FORBIDDEN })

/**
 * `/settings` (UX only, the API enforces every section's permission): open to whoever holds a
 * permission some section is for; everyone else gets the route's "No access" state.
 */
export function requireHub(session: SessionInfo): void {
  if (!canOpenHub(session.permissions)) throw forbidden()
}

/**
 * A section's own address: its view permission (tax rates, modules, regional: anyone signed in), and
 * the company's module for a module's section (Tasks). A section whose module is off, or whose view
 * permissions all belong to switched-off modules, says it is turned off (MODULE_DISABLED, audit L13).
 */
export function requireSection(session: SessionInfo, id: SectionId): void {
  const section = sectionDef(id)
  if (canOpenSection(session.permissions, section, session.modules)) return
  if (section.module !== undefined && !moduleEnabled(session.modules, section.module)) {
    throw new ApiError({ status: 403, code: ErrorCode.MODULE_DISABLED })
  }
  throw refusal(session, section.view ?? [])
}

/**
 * A working page the hub links to and hosts (Message templates, Message outbox): the link's own
 * permission, refused as "turned off" when that permission's module is off.
 */
export function requireLink(session: SessionInfo, id: LinkId): void {
  const link = SETTINGS_GROUPS.flatMap((g) => g.items).find(
    (item): item is LinkDef => item.kind === 'link' && item.id === id,
  )
  if (!link) throw new Error(`Unknown settings link: ${id}`)
  if (!session.permissions.includes(link.permission)) throw refusal(session, [link.permission])
}
